/**
 * 個人情報取り扱い同意ページ
 *
 * privacy_consent_at が null の場合に ProtectedRoute からリダイレクトされる。
 * 同意後、パスワード変更（must_change_password=true の場合）またはホームへ遷移する。
 */

import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/atoms";
import { useAcceptPrivacyConsentMutation, useGetMeQuery } from "../shared/api/endpoints/authApi";
import { PRIVACY_CONSENT_TITLE, PRIVACY_CONSENT_INTRO, PRIVACY_CONSENT_SECTIONS } from "@shift-management/shared";

const LANGUAGE_OPTIONS = [
    { value: "ja", label: "日本語" },
    { value: "en", label: "英語" },
];

const GlobeIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
    </svg>
);

const TriangleDownIcon = ({ className }: { className?: string }) => (
    <svg viewBox="0 0 10 6" fill="currentColor" xmlns="http://www.w3.org/2000/svg" className={className}>
        <path d="M5 6L0 0H10L5 6Z" />
    </svg>
);

const PrivacyConsentPage = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [acceptConsent] = useAcceptPrivacyConsentMutation();
    const { refetch } = useGetMeQuery();
    const [error, setError] = useState<string | null>(null);
    const [language, setLanguage] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);

    const handleAccept = useCallback(async () => {
        setError(null);
        setIsProcessing(true);
        try {
            await acceptConsent().unwrap();
            await refetch();
            if (user?.mustChangePassword) {
                navigate("/auth/change-password", { replace: true });
            } else {
                navigate("/home", { replace: true });
            }
        } catch {
            setIsProcessing(false);
            setError("同意処理に失敗しました。もう一度お試しください。");
        }
    }, [acceptConsent, refetch, user, navigate]);

    const handleDecline = useCallback(() => {
        logout();
    }, [logout]);

    return (
        <div className="h-svh flex flex-col overflow-hidden bg-[#EEF6F4]">
                <PageHeader
                    title={PRIVACY_CONSENT_TITLE}
                    rightContent={
                        <div className="relative">
                            <GlobeIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                            <select
                                value={language}
                                onChange={(e) => setLanguage(e.target.value)}
                                className="appearance-none bg-[#EEF6F4] border border-gray-300 rounded pl-9 pr-7 py-1 text-sm text-gray-700 cursor-pointer focus:outline-none"
                            >
                                <option value="">Languages</option>
                                {LANGUAGE_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            <TriangleDownIcon className="absolute right-2.5 top-1/2 -translate-y-1/2 w-2.5 h-2.5 text-gray-500 pointer-events-none" />
                        </div>
                    }
                />

                {/* 本文 */}
                <div className="flex-1 min-h-0 px-6 pb-6">
                    <div className="border border-gray-200 rounded-lg bg-white h-full overflow-y-auto">
                        <div className="p-6 space-y-4 text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                            <p>{PRIVACY_CONSENT_INTRO}</p>
                            {PRIVACY_CONSENT_SECTIONS.map((section) => (
                                <section key={section.heading}>
                                    <h2 className="font-bold text-gray-900 mt-4 mb-1">{section.heading}</h2>
                                    {section.paragraphs.map((text, i) => (
                                        <p key={i} className="mt-2">{text}</p>
                                    ))}
                                </section>
                            ))}
                        </div>
                    </div>
                </div>

                {/* エラー表示 */}
                {error && (
                    <div className="px-6">
                        <div className="rounded-lg bg-error-50 border border-error-200 p-3">
                            <p className="text-sm text-error-700">{error}</p>
                        </div>
                    </div>
                )}

                {/* フッター */}
                <div className="flex items-center justify-end gap-3 px-6 py-4 flex-shrink-0">
                    <Button variant="outline" size="md" onClick={handleDecline} disabled={isProcessing}>
                        同意しない
                    </Button>
                    <Button variant="primary" size="md" onClick={handleAccept} isLoading={isProcessing} disabled={isProcessing}>
                        個人情報の取り扱いに同意して利用を開始する
                    </Button>
                </div>
            </div>
    );
};

export default PrivacyConsentPage;
